import {
  dailyScoreLedgerRepository,
  habitRepository,
  logRepository,
} from '../db/database';
import type { DailyScoreLedger, IsoDate, ProgressState } from '../models/types';
import {
  getOrCreateProgressState,
  isChallengeCompletedOn,
  isFrozenDay,
} from '../services/dailyRollover';
import { isDayStreakEligible } from '../services/scoring';
import { todayIso } from '../utils/date';
import { renderIcon } from '../ui/icons';
import type { RouteId } from '../ui/tabBar';

/**
 * Streak Detay ekranı — Anasayfa'daki alev rozetinden açılır.
 *
 * İki şey gösterir: mevcut serinin büyük hâli (hero kartı) ve içinde
 * bulunulan ayın takvimi. Takvimdeki her gün, o gün kapanırken
 * `dailyScoreLedger`'a yazılmış olan `wasStreakEligible` kararından
 * gelir — bugün yeniden hesaplanmaz (§7.2/§7.3).
 */

const WEEKDAY_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const MONTH_NAMES = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

/* ==================================================================
   Takvim hücrelerinin durumları
   ================================================================== */

/**
 * Bir takvim gününün görünümü.
 *
 * - `eligible`   : gün kapandı ve streak'e sayıldı.
 * - `missed`     : gün kapandı, streak'e sayılmadı.
 * - `vacation`   : tatil hakkı kullanıldı — streak korundu ama
 *                  kullanıcının çabasıyla değil, hakla (§7.7). Ayrı
 *                  renk veriyoruz ki seri geçmişi dürüst okunsun.
 * - `today`      : bugün; gün bitmediği için karar "şimdilik"tir ve
 *                  canlı hesaplanır.
 * - `untracked`  : o gün uygulama henüz kullanılmıyordu — deftere
 *                  kayıt düşmemiş. Kırmızı göstermek haksız olurdu.
 * - `future`     : henüz gelmemiş gün.
 */
type DayStatus =
  | 'eligible'
  | 'missed'
  | 'vacation'
  | 'today'
  | 'untracked'
  | 'future';

interface CalendarCell {
  /** Ayın kaçıncı günü. Boş hücrelerde (ayın başındaki hizalama) `null`. */
  day: number | null;
  status: DayStatus;
}

interface StreakPageData {
  progress: ProgressState;
  today: IsoDate;
  /** Bugün şu ana kadarki işaretlemelerle streak'e sayılıyor mu? */
  isTodayEligible: boolean;
  monthLabel: string;
  cells: CalendarCell[];
}

/* ==================================================================
   Veri yükleme
   ================================================================== */

/**
 * Bugünün streak durumunu CANLI hesaplar.
 *
 * Bugün henüz kapanmadığı için deftere kaydı yoktur; hero kartının
 * rengi ve takvimdeki bugün hücresi bu hesaba dayanır. Kullanıcı
 * gün içinde alışkanlık işaretledikçe sonuç değişebilir — zaten amaç
 * da "bugünü kurtarmak için ne kadar kaldı" hissini vermek.
 */
async function calculateTodayEligibility(today: IsoDate): Promise<boolean> {
  const [dayLogs, allHabits, vacation, challengeCompletedToday] =
    await Promise.all([
      logRepository.listByDate(today),
      habitRepository.list(),
      isFrozenDay(today),
      isChallengeCompletedOn(today),
    ]);

  return isDayStreakEligible(dayLogs, allHabits, {
    date: today,
    isFrozenDay: vacation,
    challengeCompletedToday,
  });
}

/** Bir tarihin ay içindeki gün numarasını `yyyy-mm-dd`'ye çevirir. */
function buildDateString(year: number, monthIndex: number, day: number): IsoDate {
  const month = String(monthIndex + 1).padStart(2, '0');
  return `${year}-${month}-${String(day).padStart(2, '0')}`;
}

/**
 * İçinde bulunulan ayın takvim hücrelerini üretir.
 *
 * Hafta Pazartesi'den başlar (Türkiye'de takvimler böyle okunur).
 * `getDay()` Pazar'a 0 verdiği için `(gün + 6) % 7` ile Pazartesi'yi
 * 0'a kaydırıyoruz — ayın ilk gününün önüne kaç boş hücre koyacağımız
 * bu sayıdan çıkıyor.
 */
function buildCalendarCells(
  today: IsoDate,
  ledgerByDate: Map<IsoDate, DailyScoreLedger>,
  isTodayEligible: boolean,
): CalendarCell[] {
  const now = new Date(`${today}T12:00:00`);
  const year = now.getFullYear();
  const monthIndex = now.getMonth();

  // Bir sonraki ayın "0. günü" = bu ayın son günü.
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const firstWeekday = new Date(year, monthIndex, 1).getDay();
  const leadingEmptyCount = (firstWeekday + 6) % 7;

  const cells: CalendarCell[] = Array.from(
    { length: leadingEmptyCount },
    () => ({ day: null, status: 'future' as DayStatus }),
  );

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = buildDateString(year, monthIndex, day);
    cells.push({ day, status: resolveDayStatus(date, today, ledgerByDate, isTodayEligible) });
  }

  return cells;
}

/** Tek bir günün hangi durumda gösterileceğine karar verir. */
function resolveDayStatus(
  date: IsoDate,
  today: IsoDate,
  ledgerByDate: Map<IsoDate, DailyScoreLedger>,
  isTodayEligible: boolean,
): DayStatus {
  if (date > today) return 'future';
  if (date === today) return isTodayEligible ? 'eligible' : 'today';

  const entry = ledgerByDate.get(date);
  // Kayıt yoksa uygulama o gün henüz kullanılmıyordu.
  if (!entry) return 'untracked';
  if (entry.wasVacationDay) return 'vacation';
  return entry.wasStreakEligible ? 'eligible' : 'missed';
}

async function loadStreakPageData(): Promise<StreakPageData> {
  const today = todayIso();

  const [progress, ledgerEntries, isTodayEligible] = await Promise.all([
    getOrCreateProgressState(),
    dailyScoreLedgerRepository.list(),
    calculateTodayEligibility(today),
  ]);

  const ledgerByDate = new Map(ledgerEntries.map((entry) => [entry.date, entry]));
  const now = new Date(`${today}T12:00:00`);

  return {
    progress,
    today,
    isTodayEligible,
    monthLabel: `${MONTH_NAMES[now.getMonth()] ?? ''} ${now.getFullYear()}`,
    cells: buildCalendarCells(today, ledgerByDate, isTodayEligible),
  };
}

/* ==================================================================
   HTML
   ================================================================== */

function renderHero(data: StreakPageData): string {
  // Bugün streak'e sayılıyorsa yeşil, sayılmıyorsa turuncu — kullanıcı
  // ekrana bakar bakmaz günün durumunu anlasın diye (§5 token'ları).
  const modifier = data.isTodayEligible ? 'streak-hero--safe' : 'streak-hero--at-risk';

  return `
    <section class="streak-hero ${modifier}">
      <span class="streak-hero__flame">${renderIcon('flame', { size: 96, color: 'white', strokeWidth: 1.4 })}</span>
      <span class="streak-hero__count">${data.progress.currentStreak}</span>
    </section>
    <p class="streak-hero__caption">
      ${
        data.isTodayEligible
          ? 'Bugün streak’e sayılıyor.'
          : 'Bugün henüz streak’e sayılmıyor — alışkanlıklarının en az yarısını tamamla.'
      }
    </p>
  `;
}

function renderCalendar(data: StreakPageData): string {
  const weekdays = WEEKDAY_LABELS.map(
    (label) => `<span class="calendar__weekday">${label}</span>`,
  ).join('');

  const days = data.cells
    .map((cell) => {
      if (cell.day === null) {
        return '<span class="calendar-day calendar-day--empty"></span>';
      }
      return `<span class="calendar-day calendar-day--${cell.status}">${cell.day}</span>`;
    })
    .join('');

  return `
    <section class="calendar-card">
      <h2 class="calendar__month">${data.monthLabel}</h2>
      <div class="calendar__weekdays">${weekdays}</div>
      <div class="calendar__grid">${days}</div>
    </section>
  `;
}

/* ==================================================================
   Giriş noktası
   ================================================================== */

export async function renderStreakPage(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
): Promise<void> {
  const data = await loadStreakPageData();

  container.innerHTML = `
    <header class="detail-header">
      <button type="button" class="back-button" data-back aria-label="Anasayfaya dön">
        <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true" focusable="false">
          <path d="M7 1L1 7l6 6" stroke="currentColor" stroke-width="2" fill="none"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <h1 class="detail-header__title">Day Streak</h1>
    </header>
    ${renderHero(data)}
    ${renderCalendar(data)}
  `;

  container.querySelector('[data-back]')?.addEventListener('click', () => {
    void navigate('home');
  });
}
